import SiteHeader from "@/components/common/SiteHeader";
import { Link, createFileRoute } from "@tanstack/react-router";
import React from "react";

const ToS = () => (
  <div className="flex flex-col w-full">
    <SiteHeader header="Terms of Service" />
    <div className="px-10 py-8">
      <div className="py-2">
        <b>Last Updated: April 2, 2024</b>
      </div>
      <div>
        Welcome to Carbon Clear, a leading SaaS platform dedicated to empowering
        energy auditors with comprehensive solutions for conducting energy usage
        and carbon emissions audits in buildings. Our services are designed to
        streamline the audit process, offering tools and insights to facilitate
        efficient, accurate assessments.
      </div>

      <h2 className="py-2">1. Introduction and Acceptance of Terms</h2>
      <div>
        By accessing or using our services, website, and software (collectively,
        the “Service”), you (“User,” “you,” or “your”) agree to be bound by
        these Terms of Service (“Terms”). If you are using the Service on behalf
        of an organization or entity, then you are agreeing to these Terms on
        behalf of that organization and you represent and warrant that you have
        the authority to bind the organization to these Terms. If you do not
        agree with these Terms, you must not use our Service.
      </div>
      <div>
        These Terms govern your use of our Service and any related services
        provided by Carbon Clear. Carbon Clear reserves the right to modify,
        amend, or update these Terms at any time, with or without prior notice.
        By continuing to use the Service after such changes, you agree to be
        bound by the modified Terms.
      </div>

      <h2 className="py-2">2. User Account and Access </h2>
      <div>
        To access certain features of the Service, you must register and create
        an account. When creating your account, you agree to provide accurate,
        current, and complete information about yourself and organization as
        prompted by our registration forms. You are solely responsible for
        maintaining the confidentiality of your account information, including
        your password, and for all activities that occur under your account.
      </div>
      <div>
        You agree to notify Carbon Clear immediately of any unauthorized use of
        your account or password, or any other breach of security. Carbon Clear
        cannot and will not be liable for any loss or damage arising from your
        failure to comply with this section.
      </div>
      <h2 className="py-2">3. Intellectual Property Rights</h2>
      <div>
        All content included on the Service, such as text, graphics, logos,
        images, as well as the compilation thereof, and any software used on the
        Service, is the property of Carbon Clear or its suppliers and protected
        by copyright and other laws that protect intellectual property and
        proprietary rights. You agree to observe and abide by all copyright and
        other proprietary notices, legends, or other restrictions contained in
        any such content and will not make any changes to it.
      </div>
      <div>
        You are granted a non-exclusive, non-transferable, revocable license to
        access and use the Service strictly in accordance with these Terms. This
        license does not include any resale or commercial use of any Service or
        its contents; any collection and use of any product listings,
        descriptions, or prices; any derivative use of any Service or its
        contents; any downloading, copying, or other use of account information
        for the benefit of any third party; or any use of data mining, robots,
        or similar data gathering and extraction tools.
      </div>
      <div>
        All rights not expressly granted to you in these Terms are reserved and
        retained by Carbon Clear or its licensors, suppliers, publishers, rights
        holders, or other content providers. No part of the Service, nor any use
        of the Service, is intended to confer any license or right under any
        copyright, patent, trademark, or other intellectual property rights of
        Carbon Clear or any third party.
      </div>

      <h2 className="py-2">4. Data Handling and Privacy</h2>
      <div>
        {`Your privacy is important to us. It is Carbon Clear's policy to respect
        your privacy regarding any information we may collect while operating
        our Service. Accordingly, we have developed a Privacy Policy in order
        for you to understand how we collect, use, communicate, disclose, and
        otherwise make use of personal information. We encourage you to review
        our Privacy Policy, which is available `}
        <Link to="/policies/privacy">here</Link>.
      </div>
      <div>
        You agree that your use of the Service is subject to our Privacy Policy
        and that Carbon Clear may process personal information you provide to us
        according to the terms of our Privacy Policy, which may be amended from
        time to time.
      </div>

      <h2 className="py-2">5. Payment Terms</h2>
      <div>
        If you purchase any services that we offer for a fee, either on a
        one-time or subscription basis, you agree to Carbon Clear storing your
        payment card information. You also agree to pay the applicable fees for
        the services (including, without limitation, periodic fees for premium
        accounts) as they become due plus all related taxes, and to reimburse us
        for all collection costs and interest for any overdue amounts.
      </div>
      <div>
        All fees and charges are nonrefundable and there are no refunds or
        credits for partially used periods, except as required by applicable
        law. Carbon Clear may change the fees for the services at any time or
        impose additional fees or charges.
      </div>

      <h2 className="py-2">7. Termination of Service</h2>
      <div>
        In addition to any other legal or equitable remedies, we may, without
        prior notice to you, immediately terminate or revoke any or all of your
        permissions granted under these Conditions of Use. Upon such termination
        or revocation, you shall immediately cease all access to and use of
        Carbon Clear Services in whole or in part.
      </div>
      <div>
        Any termination of these Conditions of Use shall not affect the
        respective rights and obligations (including without limitation, payment
        obligations) of the parties arising before the date of termination.
      </div>

      <h2 className="py-2">8. Disclaimers and Limitation of Liability</h2>
      <div>
        {`The Service is provided "as is". Carbon Clear and its suppliers and
        licensors hereby disclaim all warranties of any kind, express or
        implied, including, without limitation, the warranties of
        merchantability, fitness for a particular purpose and non-infringement.
        Neither Carbon Clear nor its suppliers and licensors, makes any warranty
        that the Service will be error free or that access thereto will be
        continuous or uninterrupted.`}
      </div>
      <div>
        In no event will Carbon Clear, or its suppliers or licensors, be liable
        with respect to any subject matter of this agreement under any contract,
        negligence, strict liability or other legal or equitable theory for: (i)
        any special, incidental or consequential damages; (ii) the cost of
        procurement for substitute products or services; (iii) for interruption
        of use or loss or corruption of data.
      </div>

      <h2 className="py-2">9. Dispute Resolution</h2>
      <div>
        These Terms shall be governed by and construed in accordance with the
        laws of New York City, NY, USA, without regard to its conflict of law
        provisions. You agree to submit to the personal jurisdiction of the
        courts located within New York City, NY, USA for the purpose of
        litigating all such claims or disputes.
      </div>

      <h2 className="py-2">10. Contact Information</h2>
      <div>
        For any questions regarding these Terms or the Service, please contact
        us at{" "}
        <a href="mailto:support@getcodeclear.com">
          support@getcodeclear.com.
        </a>
      </div>
    </div>
  </div>
);

export const Route = createFileRoute("/policies/tos")({
  component: ToS,
});
